import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Basic validation
    if (!email) {
      setError('Please enter your email address.');
      setIsSubmitting(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      setSuccess(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-[#e4fde1] text-[#254e70] min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#254e70]/10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#254e70] mb-4">
              Check Your Email
            </h1>
            <p className="text-[#254e70]/70 mb-6">
              We've sent a password reset link to <strong>{email}</strong>. 
              Please check your email and follow the instructions to reset your password.
            </p>
            <p className="text-sm text-[#254e70]/60 mb-6">
              If you don't see the email, check your spam folder or try again with a different email address.
            </p>
            <Link 
              to="/login"
              className="inline-block bg-[#254e70] text-white px-6 py-3 rounded-lg hover:bg-[#1d3e5a] transition-colors duration-200"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#e4fde1] text-[#254e70] min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#254e70]/10">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-[#254e70] mb-2">
              Reset Password
            </h1>
            <p className="text-[#254e70]/70">
              Enter your email address and we'll send you a reset link
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="font-semibold text-[#254e70] text-sm">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                className="px-4 py-3.5 border-2 border-gray-200 rounded-lg text-[#254e70] focus:outline-none focus:border-[#9dacff] transition-colors duration-200"
                required
                disabled={isSubmitting}
                placeholder="Enter your email address"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#254e70] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#1d3e5a] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending Reset Link...
                </div>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-[#254e70]/70">
              Remember your password?{' '}
              <Link to="/login" className="text-[#9dacff] hover:underline font-medium">
                Back to Login
              </Link>
            </p>
            <p className="text-sm text-[#254e70]/70">
              Don't have an account?{' '}
              <Link to="/join" className="text-[#9dacff] hover:underline font-medium">
                Sign up as a therapist
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="text-[#254e70]/70 hover:text-[#254e70] text-sm transition-colors duration-200"
          >
            ← Back to TherapyList
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage; 